import type { PlainSearchParameters } from 'algoliasearch-helper';
import algoliasearchHelper from 'algoliasearch-helper';
import type {
  SearchClient,
  SearchResponse,
} from 'algoliasearch-helper/types/algoliasearch';
import { useEffect, useState } from 'react';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useSearchClient } from '@sorare/core/src/hooks/search/useSearchClient';

type Props = {
  sport: Sport;
  index?: string;
  attribute: string;
  values?: string[];
  params?: PlainSearchParameters;
  skip?: boolean;
};

const EMPTY: Record<string, number> = {};

const facetCounts = async (
  searchClient: SearchClient,
  index: string,
  props: Omit<Props, 'skip' | 'index'>
) => {
  const { attribute, values, params } = props;

  const baseParams = algoliasearchHelper(searchClient, index, {
    ...params,
  }).getQuery();

  const filteredORedAttribute = [...new Set(values)].map(
    v => `${attribute}:${v}`
  );

  return searchClient
    .search([
      {
        indexName: index,
        params: {
          ...baseParams,
          analytics: false,
          facets: [attribute],
          distinct: false,
          hitsPerPage: 0,
          page: 0,
          maxValuesPerFacet:
            values?.length || baseParams.maxValuesPerFacet || 10,
          facetFilters: [
            filteredORedAttribute,
            ...((baseParams.facetFilters || []) as string[]),
          ],
        },
      },
    ])
    .then(({ results }) => {
      return (results[0] as SearchResponse<unknown>).facets?.[attribute] || {};
    });
};

export const useFacetCounts = (props: Props) => {
  const { index, skip, attribute, params, values, sport } = props;
  const [counts, setCounts] = useState<Record<string, number>>(EMPTY);
  const algoliaSearchClient = useSearchClient();
  const searchClient = algoliaSearchClient;

  useEffect(() => {
    if (!index || skip) {
      return;
    }

    facetCounts(searchClient, index, {
      params,
      attribute,
      values,
      sport,
    }).then(res => {
      setCounts(res);
    });
  }, [
    attribute,
    index,
    params,
    algoliaSearchClient,
    skip,
    values,
    searchClient,
    sport,
  ]);

  if (!index || skip) {
    return EMPTY;
  }

  return counts;
};
